/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { SurveyStats } from '../../api/initiative-stats';
import { UserWithStats } from '../../api/initiative-stats';
import { useAppSelector } from '../../reducers';
import G17Client from '../../services/G17Client';
import { addSiteAlert, SiteAlertColors } from '../../slice/siteAlertsSlice';
import { BulkSurveysMessageForm } from './partials/BulkSurveysMessageForm';
import { BulkUsersMessageForm } from './partials/BulkUsersMessageForm';
import { QuestionMessageForm } from './partials/QuestionMessageForm';
import { SurveyMessageForm } from './partials/SurveyMessageForm';
import { UserMessageForm } from './partials/UserMessageForm';
import { MessageFormProps, MessageFormFields, OnChangeInterface, MessagePayload, MessageType } from './types';
import { isFormValid } from './utils';
import { QUESTION } from '../../constants/terminology';

interface CommonMessageModalProps {
  subject?: string;
  toggle: () => void;
}

interface SurveyMessageProps extends CommonMessageModalProps {
  surveyId: string;
}

interface QuestionMessageProps extends CommonMessageModalProps {
  utrvId: string;
}

interface UserMessageProps extends CommonMessageModalProps {
  userIds: string[];
  users?: UserWithStats[];
}

interface BulkUsersMessageProps extends CommonMessageModalProps {
  users: UserWithStats[];
}

interface BulkSurveysMessageProps extends CommonMessageModalProps {
  surveys: SurveyStats[];
}

interface MessageModalProps extends CommonMessageModalProps {
  type: MessageType;
  title?: string;
  Component: (props: MessageFormProps) => JSX.Element;
};

type ExtendedMessageModalProps = MessageModalProps & (SurveyMessageProps | QuestionMessageProps | UserMessageProps | BulkUsersMessageProps | BulkSurveysMessageProps);

const getInitialState = (props: ExtendedMessageModalProps): MessageFormFields => {
  return {
    userIds: ('userIds' in props) ? props.userIds : [],
    utrvId: ('utrvId' in props) ? props.utrvId : undefined,
    surveyId: ('surveyId' in props) ? props.surveyId : undefined,
    contributorIds: [],
    verifierIds: [],
    subject: props.subject ?? '',
    message: '',
    sendByEmail: true
  };
}

export const SurveyMessageModal = (props: SurveyMessageProps) => {
  return (
    <MessageModal
      {...props}
      type={MessageType.Survey}
      Component={(modalProps) => <SurveyMessageForm {...modalProps} {...props} />}
    />
  );
}

export const BulkSurveysMessageModal = (props: BulkSurveysMessageProps) => {
  return (
    <MessageModal
      {...props}
      type={MessageType.BulkSurvey}
      Component={(modalProps) => <BulkSurveysMessageForm {...modalProps} {...props} />}
    />
  );
}

export const QuestionMessageModal = (props: QuestionMessageProps) => {
  return (
    <MessageModal
      {...props}
      type={MessageType.Question}
      subject={`${QUESTION.CAPITALIZED_SINGULAR} Subject`}
      Component={(modalProps) => <QuestionMessageForm {...modalProps} {...props} />}
    />
  );
}

export const UserMessageModal = (props: UserMessageProps) => {
  return (
    <MessageModal
      {...props}
      type={MessageType.User}
      Component={(modalProps) => <UserMessageForm {...modalProps} {...props} />}
    />
  );
}

export const BulkUsersMessageModal = (props: BulkUsersMessageProps) => {
  return (
    <MessageModal
      {...props}
      type={MessageType.User}
      Component={(modalProps) => <BulkUsersMessageForm {...modalProps} {...props} />}
    />
  );
}

const MessageModal = (props: ExtendedMessageModalProps) => {
  const { toggle, title = 'Message Users', Component, type } = props;

  const dispatch = useDispatch();
  const [form, setForm] = useState(getInitialState(props));
  const initiativeId = useAppSelector(state => state.initiative.loaded ? state.initiative.data._id : undefined);

  if (!initiativeId) {
    return null;
  }

  const onChange: OnChangeInterface = (property) => (value) => {
    setForm(currentForm => ({
      ...currentForm,
      [property]: value
    }));
  }

  const isValid = isFormValid(type, form);

  const handleSubmit = () => {
    if (!isValid) {
      return;
    }
    const payload: MessagePayload = {
      type,
      ...form,
    };

    G17Client.sendNotificationMessage(initiativeId, payload)
      .then(() => {
        dispatch(addSiteAlert({ content: 'Message Sent', timeout: 5000 }));
        toggle();
      })
      .catch(() => {
        dispatch(addSiteAlert({
          content: 'There was an error sending your message. Please try again',
          color: SiteAlertColors.Danger
        }));
      });
  }

  return (
    <Modal isOpen={true} toggle={toggle} backdrop='static'>
      <ModalHeader toggle={toggle}>{title}</ModalHeader>
      <ModalBody>
        <Component form={form} onChange={onChange} />
      </ModalBody>
      <ModalFooter className='text-right'>
        <Button color='link' size='sm' className='text-ThemeHeadingLight mr-4' onClick={toggle}>
          Cancel
        </Button>
        <Button color='primary' disabled={!isValid} onClick={handleSubmit}>
          Send
        </Button>
      </ModalFooter>
    </Modal>
  );
}
